import { Link } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Car, Shield, Clock, Star, MapPin, Users, Plane, Building2, PartyPopper, ArrowRight } from "lucide-react";
import niceCoastlineHero from "@/assets/nice-coastline-hero.webp";
import fordMondeoImage from "@/assets/ford-mondeo-about-cropped.webp";
const services = [{
  icon: Plane,
  title: "Transfert Aéroport",
  description: "Nice Côte d'Azur, Cannes, Monaco - Service ponctuel et fiable"
}, {
  icon: Building2,
  title: "Business",
  description: "Déplacements professionnels avec discrétion et élégance"
}, {
  icon: PartyPopper,
  title: "Événements",
  description: "Mariages, soirées de gala, occasions spéciales"
}, {
  icon: MapPin,
  title: "Tourisme",
  description: "Découvrez la Côte d'Azur avec un guide local"
}];
const features = [{
  icon: Shield,
  text: "Chauffeur professionnel certifié"
}, {
  icon: Car,
  text: "Véhicules hybrides premium"
}, {
  icon: Clock,
  text: "Disponible 24h/24, 7j/7"
}, {
  icon: Star,
  text: "Service 5 étoiles garanti"
}];
const testimonials = [{
  name: "Marie L.",
  role: "Directrice Marketing",
  content: "Un service impeccable ! Le chauffeur était ponctuel et très professionnel. Je recommande vivement.",
  rating: 5
}, {
  name: "Jean-Pierre D.",
  role: "Homme d'affaires",
  content: "Parfait pour mes déplacements professionnels. Véhicules confortables et chauffeur discret.",
  rating: 5
}, {
  name: "Sophie M.",
  role: "Touriste",
  content: "Une expérience luxueuse pour notre visite de la Côte d'Azur. Merci pour ces moments inoubliables !",
  rating: 5
}];
const Index = () => {
  return <Layout>
      {/* Hero Section */}
      <section className="relative min-h-[100svh] md:min-h-[90vh] flex flex-col justify-end pb-4 md:pb-0">
        <div className="absolute inset-0">
          <img 
            src={niceCoastlineHero} 
            alt="Vue panoramique de la côte de Nice" 
            className="w-full h-full object-cover object-center"
            fetchPriority="high"
            loading="eager"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-background/90 via-background/70 to-background/40" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10 pb-32 md:pb-40">
          <div className="max-w-3xl">
            <span className="inline-block mb-2 md:mb-4 animate-fade-in font-normal text-secondary-foreground text-lg md:text-2xl">
              Service VTC Premium à Nice
            </span>
            <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold mb-4 md:mb-6 animate-fade-in" style={{
            animationDelay: "0.1s"
          }}>
              Voyagez avec
              <span className="text-gradient-gold block">Élégance</span>
            </h1>
            <p className="text-base md:text-xl text-muted-foreground mb-6 md:mb-8 animate-fade-in max-w-xl" style={{
            animationDelay: "0.2s"
          }}>
              Découvrez le luxe du transport privé sur la Côte d'Azur. 
              Véhicules hybrides, chauffeur professionnel, confort absolu.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 animate-fade-in" style={{
            animationDelay: "0.3s"
          }}>
              <Button size="lg" asChild className="text-base md:text-lg w-full sm:w-auto">
                <Link to="/reservation">
                  Réserver maintenant
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild className="text-base md:text-lg w-full sm:w-auto">
                <Link to="/tarifs">Voir nos tarifs</Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Feature badges */}
        <div className="absolute bottom-4 md:bottom-8 left-0 right-0 z-10">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
              {features.map((feature, index) => <div key={index} className="glass-effect rounded-lg p-3 md:p-4 flex items-center gap-2 md:gap-3 animate-fade-in" style={{
              animationDelay: `${0.4 + index * 0.1}s`
            }}>
                  <feature.icon className="h-4 w-4 md:h-5 md:w-5 text-primary flex-shrink-0" />
                  <span className="text-xs md:text-sm font-medium">{feature.text}</span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-12 md:py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-16">
            <span className="text-primary font-medium text-sm md:text-base">Nos Services</span>
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mt-2 mb-3 md:mb-4">
              Une solution pour chaque besoin
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-center text-sm md:text-base px-4">
              Que ce soit pour vos déplacements professionnels, touristiques ou événementiels, 
              nous vous offrons un service sur mesure.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {services.map((service, index) => <Card key={index} className="hover-lift border-border/50 bg-card/50">
                <CardContent className="p-4 md:p-6">
                  <div className="h-10 w-10 md:h-12 md:w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-3 md:mb-4">
                    <service.icon className="h-5 w-5 md:h-6 md:w-6 text-primary" />
                  </div>
                  <h3 className="font-display text-lg md:text-xl font-semibold mb-2">{service.title}</h3>
                  <p className="text-muted-foreground text-sm md:text-base">{service.description}</p>
                </CardContent>
              </Card>)}
          </div>

          <div className="text-center mt-8 md:mt-12">
            <Button variant="outline" size="lg" asChild className="w-full sm:w-auto">
              <Link to="/services">
                Découvrir tous nos services
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-12 md:py-24">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-5 gap-8 md:gap-16 items-center">
            <div className="lg:col-span-2">
              <span className="text-primary font-medium text-sm md:text-base">À propos</span>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mt-2 mb-4 md:mb-6">
                L'excellence au service de votre confort
              </h2>
              <p className="text-muted-foreground mb-4 md:mb-6 text-sm md:text-base">
                Azur Hybrid Luxe, c'est l'alliance parfaite entre le luxe et l'éco-responsabilité. 
                Notre flotte de véhicules hybrides haut de gamme vous garantit un voyage 
                confortable tout en respectant l'environnement de notre belle Côte d'Azur.
              </p>
              <ul className="space-y-3 md:space-y-4 mb-6 md:mb-8">
                <li className="flex items-center gap-3">
                  <div className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Star className="h-3 w-3 text-primary" />
                  </div>
                  <span className="text-sm md:text-base">Plus de 5 ans d'expérience sur la Côte d'Azur</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Users className="h-3 w-3 text-primary" />
                  </div>
                  <span className="text-sm md:text-base">Des milliers de clients satisfaits</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Shield className="h-3 w-3 text-primary" />
                  </div>
                  <span className="text-sm md:text-base">Chauffeur certifié et expérimenté</span>
                </li>
              </ul>
              <Button asChild className="w-full sm:w-auto">
                <Link to="/a-propos">En savoir plus</Link>
              </Button>
            </div>
            <div className="relative mt-8 lg:mt-0 lg:col-span-3">
            <div className="rounded-2xl overflow-hidden bg-muted">
                <img 
                  src={fordMondeoImage} 
                  alt="Ford Mondeo Hybride - Azur Hybrid Luxe - 5 ans d'expérience" 
                  className="w-full h-auto"
                  width={667}
                  height={375}
                  loading="lazy"
                />
              </div>
              <div className="absolute -bottom-4 -left-2 md:-bottom-6 md:-left-6 bg-primary text-primary-foreground p-4 md:p-6 rounded-xl luxury-shadow">
                <div className="text-2xl md:text-4xl font-display font-bold">5+</div>
                <div className="text-xs md:text-sm">années d'expérience</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12 md:py-24 bg-accent text-accent-foreground">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-16">
            <span className="font-medium text-primary-foreground text-2xl md:text-4xl">Témoignages</span>
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mt-2 mb-4">
              Ce que disent nos clients
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
            {testimonials.map((testimonial, index) => <Card key={index} className="bg-muted border-border">
                <CardContent className="p-4 md:p-6">
                  <div className="flex gap-1 mb-3 md:mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => <Star key={i} className="h-3 w-3 md:h-4 md:w-4 fill-primary text-primary" />)}
                  </div>
                  <p className="mb-4 md:mb-6 italic text-primary text-sm md:text-base">
                    "{testimonial.content}"
                  </p>
                  <div>
                    <div className="font-semibold text-sm md:text-base">{testimonial.name}</div>
                    <div className="text-xs md:text-sm text-primary">{testimonial.role}</div>
                  </div>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-24">
        <div className="container mx-auto px-4">
          <div className="bg-gradient-to-r from-primary/20 via-primary/10 to-primary/20 rounded-2xl md:rounded-3xl p-6 sm:p-8 md:p-12 lg:p-16 text-center">
            <h2 className="font-display text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold mb-3 md:mb-4">
              Prêt à voyager avec style ?
            </h2>
            <p className="text-muted-foreground text-sm md:text-lg mb-6 md:mb-8 max-w-2xl mx-auto px-2">
              Réservez votre chauffeur privé dès maintenant et découvrez 
              le luxe du transport VTC sur la Côte d'Azur.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
              <Button size="lg" asChild className="w-full sm:w-auto">
                <Link to="/reservation">
                  Réserver maintenant
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild className="w-full sm:w-auto">
                <a href="tel:+33614304966">
                  Appeler directement
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>;
};
export default Index;